/**
 * CommandPaletteProvider.tsx -- Context provider for the command palette.
 *
 * Centralises the CommandPalette open/close state. Registers a global
 * `keydown` listener that toggles on Cmd+K / Ctrl+K and closes on Escape.
 * Exposes `useCommandPalette(): { isOpen, open, close, toggle }`.
 *
 * Per specs/integration-plan.md Section 6.3 (T364-T365).
 *
 * @see specs/integration-plan.md Section 4.3 (utility cluster, search button)
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";

interface CommandPaletteState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const defaultState: CommandPaletteState = {
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
};

const CommandPaletteContext = createContext<CommandPaletteState>(defaultState);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Global keyboard handler: Cmd+K / Ctrl+K toggles, Escape closes
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Cmd+K or Ctrl+K -> toggle
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      // Escape -> close (only when open)
      if (e.key === "Escape") {
        setIsOpen((prev) => {
          if (prev) {
            e.preventDefault();
            return false;
          }
          return prev;
        });
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <CommandPaletteContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteState {
  return useContext(CommandPaletteContext);
}
